import { useState } from "react";
import { toast } from "sonner";
import { FileText, ExternalLink, Download, Loader2 } from "lucide-react";

// RC49-TECH-003…007 — dealer document library: categorized, useful metadata, Open/View with visible failure
const ORDER = ["Product Specifications", "Installation / Application Guidance", "Testing / Certificates / Compliance", "Maintenance / Care", "Packaging / Logistics", "Other"];

export function documentFileUrl(docNumber) {
  return `${process.env.REACT_APP_BACKEND_URL}/api/knowledge/documents/${encodeURIComponent(docNumber)}/file`;
}

// WP3-B2 Open/View correction (IR-18 / TL51-010): open synchronously inside the user gesture so popup blockers and
// mobile Safari never see an async window.open. The URL is the same-origin, cookie-authenticated API route (no
// storage URL exposed); the browser's native viewer uses HEAD/Range/206 against it. Blocked window → same-tab navigation.
export function openDocumentInline(docNumber) {
  const url = documentFileUrl(docNumber);
  const w = window.open(url, "_blank");
  if (w) { try { w.opener = null; } catch (_) { /* cross-origin guard */ } return; }
  window.location.assign(url);
}

export async function openDocumentFile(docNumber, { download = false } = {}) {
  if (!download) { openDocumentInline(docNumber); return; }
  // Download: top-level navigation to the same authorized route with ?download=1 → server answers
  // Content-Disposition: attachment, so desktop browsers and iOS Safari start a real download (Files app) and stay on the page.
  window.location.assign(`${documentFileUrl(docNumber)}?download=1`);
}

export function describeOpenError(e) {
  if (e?.message === "popup") return "Your browser blocked the document window — allow pop-ups or use Download.";
  const s = e?.response?.status;
  if (s === 403) return "This document is not available to your role.";
  if (s === 404) return "The document file is missing. Zelligio has been notified — contact your representative.";
  return "The document could not be opened. Check your connection and try again.";
}

function DocCard({ d, focused }) {
  const [busy, setBusy] = useState("");
  const [err, setErr] = useState("");
  const act = async (download) => {
    setBusy(download ? "dl" : "open"); setErr("");
    try { await openDocumentFile(d.doc_number, { download, filename: d.filename }); }
    catch (e) { const m = describeOpenError(e); setErr(m); toast.error(m); }
    finally { setBusy(""); }
  };
  return (
    <div id={`library-doc-${d.doc_number}`} className={`rounded-md border bg-white p-4 ${focused ? "border-terracotta ring-2 ring-terracotta/40" : "border-border"}`} data-testid={`library-doc-${d.doc_number}`} data-focused={focused ? "true" : undefined}>
      <div className="flex items-start gap-3">
        <FileText className="mt-0.5 h-4 w-4 shrink-0 text-terracotta" />
        <div className="min-w-0 flex-1">
          <p className="text-sm font-medium text-charcoal">{d.title}</p>
          <p className="mt-0.5 text-xs text-warmgrey">
            {d.product_scope ? `${d.product_scope} · ` : ""}Rev {d.revision}{d.issue_date ? ` · ${d.issue_date}` : ""}{d.file_type ? ` · ${d.file_type}` : ""}
          </p>
          {d.purpose && <p className="mt-1 text-xs text-warmgrey">{d.purpose}</p>}
        </div>
      </div>
      <div className="mt-3 flex flex-wrap gap-2">
        {d.has_file ? (
          <>
            <button type="button" disabled={!!busy} onClick={() => act(false)} data-testid={`library-doc-open-${d.doc_number}`}
              className="inline-flex items-center gap-1.5 rounded-full bg-terracotta px-3 py-1.5 text-xs font-medium text-white hover:bg-terracotta/90 disabled:opacity-60">
              {busy === "open" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <ExternalLink className="h-3.5 w-3.5" />} Open / View
            </button>
            <button type="button" disabled={!!busy} onClick={() => act(true)} data-testid={`library-doc-download-${d.doc_number}`}
              className="inline-flex items-center gap-1.5 rounded-full border border-terracotta px-3 py-1.5 text-xs font-medium text-terracotta hover:bg-terracotta hover:text-white disabled:opacity-60">
              <Download className="h-3.5 w-3.5" /> Download
            </button>
          </>
        ) : (
          <span className="text-xs text-warmgrey" data-testid={`library-doc-nofile-${d.doc_number}`}>No file released for this document yet.</span>
        )}
      </div>
      {err && <p className="mt-2 text-xs text-status-sold" data-testid={`library-doc-error-${d.doc_number}`}>{err}</p>}
    </div>
  );
}

export function DocumentLibrary({ documents, focusDoc = null }) {
  if (!documents.length) {
    return <p className="mt-3 rounded-md border border-dashed border-limestone p-6 text-sm text-warmgrey" data-testid="library-documents-empty">No released documents yet. Released technical files will appear here, grouped by category.</p>;
  }
  const groups = ORDER.map((c) => [c, documents.filter((d) => (d.category || "Other") === c)]).filter(([, ds]) => ds.length);
  return (
    <div className="mt-4 space-y-6" data-testid="library-documents">
      {groups.map(([cat, ds]) => (
        <section key={cat} data-testid={`library-doc-category-${cat.replace(/[^a-z]+/gi, "-").toLowerCase()}`}>
          <h3 className="text-xs font-semibold uppercase tracking-wider text-warmgrey">{cat} <span className="text-warmgrey/70">({ds.length})</span></h3>
          <div className="mt-2 grid gap-3 md:grid-cols-2">{ds.map((d) => <DocCard key={`${d.doc_number}-${d.revision}`} d={d} focused={d.doc_number === focusDoc} />)}</div>
        </section>
      ))}
    </div>
  );
}
